import { useContext, useEffect, useRef, useState } from 'react';
import { AnalyticsContext, getLocalStorage } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useUserConsent } from '../userConsent';

const VENUE_CREATE_DIALOG = (eventId?: string | null) => `${eventId}_venueCreateDialog`;

const getVenueDialogLocalStorageKey = (eventId?: string | null) => {
  return eventId ? VENUE_CREATE_DIALOG(eventId) : null;
};

/**
 * Tracks whether the user has seen the accommodations venue dialog (existing onboarding flow).
 */
export const useHasSeenOnboardingVenueDialog = () => {
  const hasTrackedTelemetryRef = useRef(false);
  const localStorage = getLocalStorage();
  const analytics = useContext(AnalyticsContext);
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();

  const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();
  const hasSeenVenueDialogFromConsentKey = getUserConsent('hasSeenOnboardingVenueDialog');

  /**
   * Checks if the user has seen the venue create dialog from localStorage.
   * Uses state to reactively track localStorage changes.
   */
  const [hasSeenVenueDialogFromStorage, setHasSeenVenueDialogFromStorage] = useState(() => {
    const localStorageKey = getVenueDialogLocalStorageKey(eventInfo?.eventId);
    return localStorage.getItem(localStorageKey ?? '') === 'hide';
  });

  // Sync state with localStorage when eventId changes
  useEffect(() => {
    const localStorageKey = getVenueDialogLocalStorageKey(eventInfo?.eventId);
    setHasSeenVenueDialogFromStorage(localStorage.getItem(localStorageKey ?? '') === 'hide');
  }, [eventInfo?.eventId, localStorage]);

  const isLoadingOnboardingVenueDialogFlag = loadingEventInfo || loadingUserConsent;

  /**
   * Marks the onboarding venue dialog as seen by setting the consentKey and cleaning up localStorage.
   * This prevents the dialog from showing on subsequent visits.
   */
  const setHasSeenOnboardingVenueDialog = useEventCallback(() => {
    // Cleanup existing localstorage feature flag, so we could early switch to useIsFeatureEnabled.
    if (hasSeenVenueDialogFromStorage) {
      const localStorageKey = getVenueDialogLocalStorageKey(eventInfo?.eventId);
      if (localStorageKey) {
        localStorage.setItem(localStorageKey, null);
        // Update state immediately to reflect localStorage change
        setHasSeenVenueDialogFromStorage(false);
      }
    }
    if (!hasSeenVenueDialogFromConsentKey) {
      setUserConsent('hasSeenOnboardingVenueDialog', true);
    }
  });

  /**
   * Tracks usage of the legacy localStorage flag for auto-populated hotels.
   * This is temporary and can be removed once the feature flag fully replaces localStorage logic.
   */
  useEffect(() => {
    if (hasSeenVenueDialogFromStorage && !hasTrackedTelemetryRef.current) {
      hasTrackedTelemetryRef.current = true;
      analytics.track({
        action: 'TravelButtonInteracted',
        category: 'wedding',
        pagePrefix: 'admin',
        extraInfo: {
          name: 'hasSeenOnboardingVenueDialogFromStorage',
        },
      });
    }
  }, [hasSeenVenueDialogFromStorage, analytics, hasTrackedTelemetryRef]);

  const hasSeenOnboardingVenueDialog = hasSeenVenueDialogFromConsentKey || hasSeenVenueDialogFromStorage;

  return {
    hasSeenOnboardingVenueDialog,
    setHasSeenOnboardingVenueDialog,
    isLoadingOnboardingVenueDialogFlag,
  };
};
